import { useState, useMemo } from 'react'
import { Clock3, RefreshCcw, Edit3, Trash2, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { optionLabel, formatDateTime } from '@/settings/master'

export default function QuestionsCard({
  questions = [],
  selectable = false,
  selected = new Set(),
  onToggleSelect = () => { },
  subjects = [],
  levelMapping = {},
  optionStyle = 'thai',
  typeSummary = 'TF',
  editingIndex = null,
  setEditingIndex = () => { },
  editedQuestion,
  setEditedQuestion = () => { },
  startEditQuestion = () => { },
  handleSaveEdit = () => { },
  handleDeleteQuestion = () => { },
  openAlert = m => window.alert(m)
}) {
  const cardVar = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }
  const listVar = { hidden: { opacity: 0, x: -6 }, show: { opacity: 1, x: 0 } }

  // โหมดย่อ/ขยายรายข้อ
  const [expanded, setExpanded] = useState(() => questions.map(() => true))
  const toggleExpand = (idx) => setExpanded(prev => prev.map((v, i) => i === idx ? !v : v))



  return (
    <AnimatePresence>
      {questions.map((q, index) => {
        const isExpanded = expanded[index] ?? true


        if (editingIndex === index && editedQuestion) {
          const updateOption = (idx, field, val) => {
            const key = Object.keys(editedQuestion.answer[idx])[0]
            const entry = editedQuestion.answer[idx][key]
            const arr = editedQuestion.answer.map((o, i) =>
              i === idx
                ? {
                  [key]:
                    field === 'desc'
                      ? (typeof entry === 'object' ? { ...entry, description: val } : val)
                      : { ...(typeof entry === 'object' ? entry : { description: entry }), points: val }
                }
                : o
            )
            setEditedQuestion(p => ({ ...p, answer: arr }))
          }

          return (
            <motion.div
              key={index}
              variants={cardVar}
              initial="hidden"
              animate="show"
              exit="hidden"
              transition={{ duration: 0.25 }}
              className="rounded-2xl border border-gray-200 bg-gray-50/80 p-5 mb-5 shadow-sm"
            >
              <input
                type="text"
                value={editedQuestion.question}
                onChange={e => setEditedQuestion(p => ({ ...p, question: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-primary mb-4 bg-white"
                placeholder="แก้ไขข้อความคำถาม..."
              />
              <ul className="space-y-3">
                {editedQuestion.answer.map((opt, i) => {
                  const key = Object.keys(opt)[0]
                  const val = opt[key]
                  const desc = typeof val === 'object' ? val.description : val
                  return (
                    <motion.li
                      key={i}
                      variants={listVar}
                      initial="hidden"
                      animate="show"
                      exit="hidden"
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      className="flex items-center gap-3"
                    >
                      <span className="text-primary font-semibold min-w-[32px] text-sm">
                        {optionLabel(i, optionStyle)}
                      </span>
                      <input
                        type="text"
                        value={desc}
                        onChange={e => updateOption(i, 'desc', e.target.value)}
                        className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-primary bg-white"
                        placeholder={`แก้ไขตัวเลือก ${i + 1}`}
                      />
                      {typeSummary === 'PO' && (
                        <input
                          type="number"
                          value={typeof val === 'object' ? val.points : 1}
                          onChange={e => updateOption(i, 'points', Number(e.target.value))}
                          className="w-20 border border-gray-300 rounded-xl px-2 py-2 text-sm focus:ring-2 focus:ring-primary bg-white"
                        />
                      )}
                      {typeSummary !== 'PO' && (
                        <label className="flex items-center gap-1 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={editedQuestion.correct.includes(key)}
                            onChange={() => {
                              let corr = [...editedQuestion.correct]
                              if (corr.includes(key)) corr = corr.filter(k => k !== key)
                              else corr = typeSummary === 'TF' ? [key] : [...corr, key]
                              if (corr.length === 0) {
                                openAlert('ต้องมีคำตอบถูกต้องอย่างน้อย 1 ค่า')
                                return
                              }
                              setEditedQuestion(p => ({ ...p, correct: corr }))
                            }}
                            className="accent-primary"
                          />
                          ถูกต้อง
                        </label>
                      )}
                    </motion.li>
                  )
                })}
              </ul>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleSaveEdit}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-5 py-2 text-sm font-semibold"
                >
                  บันทึก
                </button>
                <button
                  onClick={() => setEditingIndex(null)}
                  className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-lg px-5 py-2 text-sm font-semibold"
                >
                  ยกเลิก
                </button>
              </div>
            </motion.div>
          )
        }

        return (
          <motion.div
            key={index}
            variants={cardVar}
            initial="hidden"
            animate="show"
            exit="hidden"
            transition={{ duration: 0.25 }}
            className="rounded-2xl border border-gray-200 bg-white p-5 mb-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <button
              type="button"
              onClick={() => toggleExpand(index)}
              className="w-full text-left flex items-start justify-between gap-3"
            >
              <div className="flex-1">

                <p className="text-gray-800 text-sm leading-6">
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={selected.has(index)}
                      onClick={e => e.stopPropagation()}
                      onChange={() => onToggleSelect(index)}
                      className="accent-primary mt-1 mr-2"
                    />
                  )}

                  <span className="font-semibold">{index + 1}.</span> {q.question}{' '}
                  {!q.id && <span className="ml-2 inline-block bg-pink-600 text-white text-[10px] px-2 rounded">NEW</span>}
                </p>
                <small className="text-gray-500 text-[11px]">
                  {q.createdAt && (
                    <span className="inline-flex items-center gap-1 ml-2">
                      <Clock3 size={10} /> เพิ่ม: {formatDateTime(new Date(q.createdAt))}
                    </span>
                  )}
                  {q.updatedAt && (
                    <span className="inline-flex items-center gap-1 ml-2">
                      <RefreshCcw size={10} /> แก้ไข: {formatDateTime(new Date(q.updatedAt))}
                    </span>
                  )}
                </small>
              </div>
              <div className="shrink-0 text-gray-500 mt-1">
                {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-3"
                >
                  <ul className="space-y-2 pl-5">
                    {q.answer.map((opt, i) => {
                      const key = Object.keys(opt)[0]
                      const val = opt[key]
                      const desc = typeof val === 'object' ? val.description : val
                      const isCorr = q.correct.includes(key)
                      return (
                        <motion.li
                          key={i}
                          variants={listVar}
                          initial="hidden"
                          animate="show"
                          exit="hidden"
                          transition={{ duration: 0.2, delay: i * 0.015 }}
                          className={[
                            'flex items-center gap-2 border rounded-md px-3 py-2 text-sm bg-white',
                            isCorr ? 'border-green-500/70 bg-green-50' : 'border-gray-200'
                          ].join(' ')}
                        >
                          <span className="w-4 flex justify-center">
                            {isCorr && <CheckCircle2 size={16} className="text-green-600" />}
                          </span>
                          <span className="font-semibold text-primary min-w-[32px]">
                            {optionLabel(i, optionStyle)}
                          </span>
                          <span className="flex-1">{desc}</span>
                          {typeSummary === 'PO' && (
                            <span className="ml-auto text-xs">(น้ำหนัก: {val.points})</span>
                          )}
                        </motion.li>
                      )
                    })}
                  </ul>

                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      onClick={() => startEditQuestion(index)}
                      className="inline-flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white rounded-md px-3 py-1.5 text-xs font-semibold"
                    >
                      <Edit3 size={14} /> แก้ไข
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(index)}
                      className="inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-3 py-1.5 text-xs font-semibold"
                    >
                      <Trash2 size={14} /> ลบ
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )
      })}
    </AnimatePresence>
  )
}
